import React, { JSX, useMemo } from 'react';
import EventStatusTransitionDialogFrame from './EventStatusTransitionDialogFrame';

export type RaceTransitionCheck = {
  key: string;
  label: string;
  ok: boolean;
  detail?: string;
};

type Props = {
  open: boolean;
  eventId: string;
  eventName?: string;
  subtitleOverride?: string;
  checks: RaceTransitionCheck[];
  busy: boolean;
  onCancel: () => void;
  onProceed: () => void | Promise<void>;
};

export default function RackingToActiveConfirmDialog(props: Props): JSX.Element | null {
  const { open, eventId, eventName, subtitleOverride, checks, busy, onCancel, onProceed } = props;
  const titleName =
    (subtitleOverride?.trim() ? subtitleOverride.trim() : null) ||
    (eventName?.trim() ? eventName : eventId);

  const allChecksOk = useMemo(() => checks.length > 0 && checks.every((c) => c.ok), [checks]);

  return (
    <EventStatusTransitionDialogFrame
      open={open}
      busy={busy}
      onCancel={onCancel}
      onProceed={onProceed}
      titleId="racking-to-active-title"
      eyebrow="Race start"
      title="Move to Active"
      subtitle={titleName}
      headerAccent={allChecksOk ? 'emerald' : 'amber'}
      warningTone="danger"
      warningTitle="This operation cannot be undone"
      warningBody={
        <>
          Moving to <strong>Active</strong> starts the race from an operations perspective. Device runtime
          snapshots are taken and this transition cannot be reversed.
        </>
      }
      operationIntro={
        <p>
          Resolve any blocking items below before proceeding. At least one athlete pair must be marked{' '}
          <span className="font-semibold text-gray-800 dark:text-slate-200">racked</span> during verification.
        </p>
      }
      confirmPhrase="ACTIVE"
      confirmInputId="racking-to-active-confirm-input"
      proceedButtonText="Proceed to Active"
      proceedDisabledExtra={!allChecksOk}
      proceedUseSuccessTone={allChecksOk}
    >
      <div className="space-y-2">
        {checks.map((check) => (
          <div
            key={check.key}
            className={[
              'rounded-md border px-3 py-2 text-xs',
              check.ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/30 dark:text-emerald-100'
                : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100',
            ].join(' ')}
          >
            <div className="font-semibold">
              {check.ok ? '✓ ' : '✗ '}
              {check.label}
            </div>
            {check.detail ? <div className="mt-1 opacity-90">{check.detail}</div> : null}
          </div>
        ))}
      </div>
    </EventStatusTransitionDialogFrame>
  );
}
